/* eslint-disable import/no-extraneous-dependencies */
import React from 'react'
import { Pane, Text, TableV2, Flex, Theme } from '@leverege/ui-elements'
import { LinearView, MomentHeader, MomentBackground, ScrollSync, Projection } from '@leverege/ui-linear-view-elements'
import { GlobalState, Selection } from '@leverege/ui-redux'
import classnames from 'classnames'

import LinearViewBlock from './LinearViewBlock'
import GetStageFormat from '../../../util/Stage'

import S from './GanttTableSplitView.css'

function MyLinearViewBlock( props ) {
  const { 
    item : {
      stage : outerStage,
      obj : { data : { currentStage : innerStage } }
    }
  } = props
  
  const currentStage = outerStage ?? innerStage
  const name = GetStageFormat( currentStage ).name

  return (
    <LinearViewBlock
      {...props}>
      <Text variant="captionSmallBold">{name}</Text>
    </LinearViewBlock>
  )
}

function BayNameCell( props ) {
  const { value } = props
  const { bayName, workCellName = 'Line 1' } = value
  return (
    <Flex variant="rowS" align="center">
      <Text variant="specialBayName|detailValue">
        {bayName} - {workCellName}
      </Text>
    </Flex>
  )
}

function CenterHeader( props ) {
  const { children, ...rest } = props
  return (
    <Pane {...rest}>
      <Pane className={S.centerLinearHeader}>{children}</Pane>
    </Pane>
  )
}

const columns = [
  {
    id : 'bayName',
    Header : () => (
      <Flex className={S.bayNameHeader} align="center" justify="center">Name</Flex>
    ),
    accessor : ( { obj, workCellObj } ) => {
      const bayName = obj?.parentName
      const isLW = obj?.data?.lwLine != null

      const workCellName = isLW ? 'LP' : 'HP'

      return {
        bayName,
        workCellName
      }
    },
    Cell : BayNameCell,
    minWidth : 230,
  }
]

const DEFAULT_STYLE = {
  height : '100%',
  marginTop : 0
}

export default class TableSplitView extends React.Component {

  static contextType = Theme.Context

  constructor( props ) {
    super( props )
    this.syncRef = React.createRef()
    this.scrollRef = React.createRef()
    this.headerRef = React.createRef()
    this.linearViewRef = React.createRef()
    this.state = {
      style : {
        table : DEFAULT_STYLE,
        lv : DEFAULT_STYLE
      },
      lvHeaderHeight : 0,
      tableHeaderHeight : 0
    }
  }

  componentDidUpdate() {
    if ( !this.resizeObserver && this.linearViewRef.current ) {
      this.resizeObserver = new ResizeObserver( ( entries ) => {
        this.onResize( entries[0] )
      } )
  
      this.resizeObserver.observe( this.linearViewRef.current.contentRef.current )
    }
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect()
  }

  onResize = ( evt ) => {
    this.linearViewRef.current.onContentScroll()
  }

  onScroll = ( evt ) => { 
    if ( this.syncRef.current ) {
      this.syncRef.current.onScroll( evt )
    }
  }

  blockStylizer = ( item, projection, startValue, stopValue, x1, x2 ) => {
    const {
      stage : outerStage,
      obj : { data : { currentStage : innerStage } }
    } = item
    const currentStage = outerStage ?? innerStage

    const className = GetStageFormat( currentStage ).className ?? currentStage
    return {
      component : MyLinearViewBlock,
      componentProps : {
        variant : 'linearViewBlock',
        item,
        x1,
        x2
      },
      className : classnames( S[className], S.really, S.win ),
      style : {
        borderStyle : 'solid',
        boderWidth : '1px',
        zIndex : 2,
        borderRadius : '4px',
        paddingLeft : '8px',
        paddingRight : '13px',
        whiteSpace : 'nowrap', 
        overflow : 'hidden',
        textOverflow : 'ellipsis',
        display : 'flex',
        alignItems : 'center',
        height : '28px',
        marginTop : '-2px'
      }
    }
  }

  onHeaderHeightChange = ( height ) => {
    setTimeout( () => {
      const { lvHeaderHeight, tableHeaderHeight } = this.state

      const th = this.headerRef && this.headerRef.current ? this.headerRef.current.clientHeight : 0
  
      if ( th > height ) {
        if ( th !== tableHeaderHeight ) {
          const mt = `${th - height}px`
          this.setState( {
            style : {
              lv : {
                height : `calc(100% - ${mt})`
              },
              table : DEFAULT_STYLE
            },
            tableHeaderHeight : th
          } )
        }
      } else if ( height !== lvHeaderHeight ) {
        this.setState( { 
          style : { 
            table : {
              height : '100%',
              // marginTop : height - rh
            },
            lv : DEFAULT_STYLE
          }, 
          lvHeaderHeight : height
        } )
      }
    }, 250 )
  }

  onRollover = ( i ) => {
    const { entered, data } = i

    if ( entered ) {
      GlobalState.dispatch( Selection.set( 'rollover', { id : data.id } ) )
    } else {
      GlobalState.dispatch( Selection.clear( 'rollover' ) )
    }
  }

  onClick = ( i ) => {
    const { selected } = this.props

    const { data } = i
    const hasSelection = !Selection.isEmpty( selected )

    if ( hasSelection ) {
      GlobalState.dispatch( Selection.clear( 'selected' ) )
    } else {
      GlobalState.dispatch( Selection.set( 'selected', { id : data.id } ) )
    }
  }

  getTableRowProps = ( row, props, ...rest ) => {
    const { index, original : { obj : { data } } } = row
    const lineId = data?.uwMold?.id ?? data?.lwLine?.id
    const { onClickLabel } = this.props
    const rowStyle = { height : '40px' }
    const isOddRow = index % 2 !== 0
    if ( isOddRow ) rowStyle.backgroundColor = '#F9FAFB'
    return {
      ...props,
      style : rowStyle,
      onClick : () => onClickLabel( { locationId : '', lineId } )
    }
  }

  getLinearRowProps = ( rowData ) => {
    return {
      variant : 'linearViewRow',
      width : '100%',
    }
  }

  render() {
    const { style } = this.state
    const {
      headerData,
      rows,
      minTime,
      maxTime,
    } = this.props

    const theme = this.context

    const headerClassName = theme.subclasses( 'TableV2', 'headerCell', S.headerCell )

    const projection = new Projection( {
      elementRef : this.linearViewRef.current?.contentRef,
      pixelValue : 50000,
      startValue : minTime,
      stopValue : maxTime
    } )

    return (
      <Flex variant="row">
        <div>
          <ScrollSync 
            onHeaderHeightChange={this.onHeaderHeightChange}
            ref={this.syncRef}
            scrollRef={this.scrollRef}
            headerRef={this.headerRef}
            linearViewRef={this.linearViewRef} >
            <TableV2
              headerClassName={headerClassName}
              className={S.table}
              variant="timeline"
              paginate={false}
              style={style.table}
              onScroll={this.onScroll}
              tableRef={this.scrollRef}
              headerRef={this.headerRef}
              getRowProps={this.getTableRowProps}
              data={headerData} 
              columns={columns} />
          </ScrollSync>
        </div>
        <div className={S.root}>
          <LinearView
            contentClassName={S.content}
            style={style.lv}
            onRollover={this.onRollover}
            onClick={this.onClick}
            ref={this.linearViewRef}
            projection={projection}
            data={rows}
            scrollRef={this.syncRef}
            rowComponent={Pane}
            rowClass={S.row}
            rowProps={this.getLinearRowProps}
            blockStylizer={this.blockStylizer}
            startAccess={getStart}
            stopAccess={getStop}>
            <MomentBackground
              projection={projection}
              unit="hour"/>
            <MomentHeader
              fillWidth
              cellComponent={CenterHeader}
              cellComponentProps={{ variant : 'linearViewHeaderCell1' }}
              labelComponent={Text}
              labelComponentProps={{ variant : 'linearViewHeaderCell1' }}
              projection={projection} 
              unit="day" 
              format="MMM DD" 
              sticky/>
            <MomentHeader
              fillWidth
              cellComponent={Pane}
              cellComponentProps={{ variant : 'linearViewHeaderCell2' }}
              labelComponent={Text}
              labelComponentProps={{ variant : 'linearViewHeaderCell2' }}
              projection={projection} 
              unit="hour" 
              format="h A" 
              sticky/>
          </LinearView>
        </div>
      </Flex>
    )
  }
}

function getStart( i ) {
  return i.startTime
}
function getStop( i ) {
  return i.stopTime
}
